import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  FileText, 
  BookOpen, 
  DollarSign, 
  TrendingUp,
  Eye,
  Activity,
  Settings,
  LogOut,
  Plus,
  Search,
  Filter,
  Home,
  Image,
  Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';

const AdminOrdersPage = () => {
  const [stats, setStats] = useState(null);
    const [adminUser, setAdminUser] = useState(null);
    
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const navigate = useNavigate();

  const statuses = ['All', 'completed', 'pending', 'failed'];

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin/login');
    fetchOrders();
  }, [currentPage, searchTerm, selectedStatus]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10'
      });
      if (selectedStatus && selectedStatus !== 'All') params.append('status', selectedStatus);
      if (searchTerm) params.append('search', searchTerm);

      const res = await fetch(`https://justurbane.in/backend_php/public/api/admin/orders?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to fetch orders');

      const data = await res.json();
      setOrders(data.orders);
      setTotalPages(data.total_pages);
    } catch (err) {
      toast.error('Failed to load orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`https://justurbane.in/backend_php/public/api/admin/orders/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete order');
      toast.success('Order deleted');
      setShowDeleteModal(false);
      fetchOrders();
    } catch (err) {
      toast.error('Failed to delete order');
      console.error(err);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString('en-IN');

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-gray-600">Loading orders...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_urbane-nexus/artifacts/w4pbaa92_Untitled%20design-10.png" 
                  alt="Just Urbane" 
                  className="h-10 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div 
                  className="w-10 h-10 bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg flex items-center justify-center"
                  style={{ display: 'none' }}
                >
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Just Urbane Admin</h1>
                  <p className="text-sm text-gray-500">Master Control Panel</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>Welcome,</span>
                <span className="font-semibold text-gray-900">{adminUser?.full_name}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div className="flex space-x-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"/>
            <input type="text" placeholder="Search orders..." value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-amber-500 focus:border-transparent"/>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"/>
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg">
              {statuses.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>
        </div>
        <div className="text-sm text-gray-600">{orders.length} orders found</div>
      </div>

      {/* Orders Table */}
      <div className="max-w-7xl mx-auto px-4 py-4 bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Order ID</th>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Address</th>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Package</th>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Payment Method</th>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-2 text-xs font-medium text-gray-500 uppercase text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {orders.map(order => (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="px-4 py-2">{order.razorpay_order_id}</td>
                <td className="px-4 py-2">{order.customer_details.full_name} <br/><span className="text-xs text-gray-500">{order.customer_details.email}</span></td>
                <td className="px-4 py-2">
                    {order.customer_details.address_line_1 ||
                    order.customer_details.address_line_2 ||
                    order.customer_details.city ||
                    order.customer_details.state ||
                    order.customer_details.postal_code ? (
                        <>
                        {order.customer_details.address_line_1 && (<span className="text-xs text-gray-500">{order.customer_details.address_line_1}<br/></span>)}
                        {order.customer_details.address_line_2 && <span className="text-xs text-gray-500">{order.customer_details.address_line_2}<br/></span>}
                        {(order.customer_details.city || order.customer_details.state) && (
                            <span className="text-xs text-gray-500">
                            {order.customer_details.city || ''}
                            {order.customer_details.city && order.customer_details.state ? ', ' : ''}
                            {order.customer_details.state || ''}<br/>
                            </span>
                        )}
                        {order.customer_details.postal_code && <span className="text-xs text-gray-500">{order.customer_details.postal_code}</span>}
                        </>
                    ) : (
                        'NA'
                    )}
                    </td>

                <td className="px-4 py-2">{order.package_id}</td>
                <td className="px-4 py-2">{order.amount} {order.currency}</td>
                <td className="px-4 py-2 capitalize">{order.status}</td>
                <td className="px-4 py-2">{order.payment_method}</td>
                <td className="px-4 py-2">{formatDate(order.created_at)}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => { setOrderToDelete(order); setShowDeleteModal(true); }}
                    className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-4">
            <button disabled={currentPage===1} onClick={()=>setCurrentPage(currentPage-1)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage===totalPages} onClick={()=>setCurrentPage(currentPage+1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-sm w-full">
            <h3 className="text-lg font-bold">Delete Order</h3>
            <p className="mt-2 text-gray-600">Are you sure you want to delete order "{orderToDelete?.razorpay_order_id}"?</p>
            <div className="flex space-x-2 mt-4">
              <button onClick={()=>handleDelete(orderToDelete.id)} className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">Delete</button>
              <button onClick={()=>setShowDeleteModal(false)} className="flex-1 bg-gray-300 px-4 py-2 rounded hover:bg-gray-400">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;
